// 81 §8 — THE ADMIN VIEW BECOMES AN INCIDENT SCREEN.
//
// §8: "[MEASURED — what brief 80 actually shipped, PR #369] an admin-gated read
//  of a colleague's factors, and removal ONE ROW AT A TIME. THAT IS A LIST, NOT
//  AN INCIDENT SCREEN, and this brief makes it one. Do not assume the behaviour
//  below already exists.
//
//  The admin view becomes: what a colleague has enrolled, WHERE AND WHEN IT HAS
//  BEEN USED, and —
//    · Revoke everything, AS ONE ACT. Removing rows one at a time is the
//      procedure that misses one, and the credential missed is the one still
//      open.
//    · End live sessions IN THE SAME PLACE. Revoking factors while a session
//      runs leaves the holder signed in.
//    · Built so a NEW CREDENTIAL CLASS APPEARS HERE BY CONSTRUCTION."
//
// ── THE THIRD BULLET IS ABOUT THIS FILE AS MUCH AS THE ROUTE ───────────────
//
// The revoke verb is total at the server (`DELETE /api/auth-factors/of/:id`
// deletes by ABSENCE — everything except the non-credential `password` row), so
// a passkey or a trusted device is swept the day it exists. This screen must not
// undo that by ENUMERATING types it knows: it lists whatever rows come back and
// counts them, so a shape nobody here has heard of is still shown and still
// revoked. There is no `if (type === 'totp')` in this file, deliberately.
//
// ── WHAT IT SHOWS, AND WHAT IT SAYS ABOUT IT ───────────────────────────────
//
// The colleague's factors AND his attempt log, on one screen, because §8's
// "where and when it has been used" is the second of those and brief 80's screen
// had only the first. §1's caveat rides with the location, as everywhere else.
//
// AND OPENING IT IS RECORDED. §6: "An administrator's read of somebody else's
// record is itself recorded, and appears in that person's own view." The server
// writes that notification; this screen SAYS SO before the administrator opens
// anything, because a trail somebody does not know about is a trap rather than
// a deterrent.
import { useState } from "react";
import { useTranslation } from "../i18n";
import { FS } from "../lib/tokens";
// BRIEF 105 §1a — the add affordance every other screen uses.
import { AddButton } from "./SharedUI";
// BRIEF 105 §1b — the draft. `openCompose` is the machinery the parties pane
// rides on and it routes per the reader's own `emailClient` preference.
import { openCompose } from "../lib/outlookCompose";
import { buildInviteMail } from "../lib/enrolmentInviteMail";
import { usePreferences } from "../hooks/usePreferences";
import {
  listAuthFactorsOf,
  revokeAllAuthFactors,
  getAuthAttemptsOf,
  killPerson,
  revivePerson,
  // BRIEF 96 §3a — the sending half of the enrolment invitation.
  sendEnrolmentInvitation,
  // BRIEF 98 §5 — 81 §5's fourth clause, finished.
  removeTrustedDevice,
  TRUSTED_DEVICE_TYPE,
} from "../api";
import type { AuthFactorRow, AuthFactorsOfResponse, AuthAttemptRow, AuthAttemptsResponse } from "../api";
import type { CollaboratorLike } from "../lib/authShapes";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`

type Toast = (message: string) => void;
type ApiFailure = { message?: string; code?: string | null };

/** 96 §3b — what an administrator is handed, once, and never again. */
type InviteHandout = {
  collaboratorId: number;
  url: string;
  expiresAt: string;
  /** BRIEF 103 §1 — HOURS. It was `days` until the window stopped being one. */
  hours: number;
  witness: string;
  /** BRIEF 105 §1b-i — the address the draft was opened to, if there was one. */
  to: string;
};
/** 96 §3b — a REFUSAL is not an error, and the two do not look alike. */
type InviteRefusal = { collaboratorId: number; text: string; refusal: boolean };

/**
 * Everything that is a login credential.
 *
 * NOT A LIST OF TYPES — the same by-absence rule the server's revoke uses, and
 * for the same reason: `recovery_code` rows are ten rows of one fact (the count
 * says it) and the `password` row is 77 §4b's expiry carrier and not somebody's
 * to remove. Anything else is a credential, including one this build has never
 * heard of.
 */
const credentials = (rows: AuthFactorRow[] | undefined): AuthFactorRow[] =>
  (rows || []).filter((r) => r.type !== "recovery_code" && r.type !== "password");

export default function AuthIncidentPanel({
  collaborators,
  isAdmin,
  killed,
  onKillChange,
  toast,
}: {
  collaborators?: CollaboratorLike[];
  isAdmin?: boolean;
  killed?: number[];
  onKillChange?: () => void;
  toast?: Toast;
}) {
  const { t, tIn, locale, supported } = useTranslation();
  /* §1b-i — the reader's own email client, the same preference the parties
     pane and the pro-forma send flow read. Not a new setting and not a new
     default: `openCompose` falls back to `mailto:` on its own. */
  const { prefs } = usePreferences();
  const emailClient = prefs.emailClient || "mailto";
  const [openId, setOpenId] = useState<number | null>(null);
  const [factors, setFactors] = useState<AuthFactorsOfResponse | null>(null);
  const [log, setLog] = useState<AuthAttemptsResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmRevoke, setConfirmRevoke] = useState<number | null>(null);
  /* BRIEF 96 §3b — THE TOKEN LIVES IN THIS STATE AND NOWHERE ELSE.
     It is a credential: the route answers it exactly once (`authFactors.js:676`
     — every later read answers a row holding a hash), so there is no way to
     fetch it back and no reason to keep it. It is not written to
     `localStorage`, not put in the URL, and it is dropped the moment the person
     is collapsed or another invitation is issued. Closing the panel loses it,
     which is correct: re-issuing is one click and REPLACES the outstanding
     one rather than adding a second way in. */
  const [invite, setInvite] = useState<InviteHandout | null>(null);
  const [inviteRefusal, setInviteRefusal] = useState<InviteRefusal | null>(null);

  if (!isAdmin) return null;

  const say = (e: unknown, sentence: string) =>
    setErr(String((e as ApiFailure)?.message || "").trim() || sentence);

  const open = async (id: number) => {
    if (openId === id) { setOpenId(null); setFactors(null); setLog(null); setInvite(null); setInviteRefusal(null); return; }
    setOpenId(id); setFactors(null); setLog(null); setErr(""); setConfirmRevoke(null);
    // §3b — the token does not survive a change of subject.
    setInvite(null); setInviteRefusal(null);
    setBusy(true);
    try {
      // Both, together: "what a colleague has enrolled, WHERE AND WHEN IT HAS
      // BEEN USED". One of the two is not an incident screen.
      const [f, l] = await Promise.all([listAuthFactorsOf(id), getAuthAttemptsOf(id)]);
      setFactors(f);
      setLog(l);
    } catch (e) {
      say(e, t("incident.load_failed", { fallback: "Could not read that person's sign-in record." }));
    } finally {
      setBusy(false);
    }
  };

  /* BRIEF 96 §3a/§3b — SEND ONE, AND SHOW WHAT CAME BACK.
     The route mails nothing; it hands the token to its caller, so this is the
     screen that turns it into something an administrator can pass on. The link
     is `?invite=<token>` at this origin, which is what `LoginView.tsx:31` reads
     and hands to `InvitationView`. */
  /* BRIEF 105 §1b-i — AND THE DRAFT, WHICH IS A SECOND ACT ON THE SAME CLICK.
     Split out so the panel can offer it AGAIN below: `window.open` needs
     transient user activation, and the call that follows the round trip above
     is spending the activation of a click that happened up to a few hundred
     milliseconds ago. Chrome's window is five seconds and the request is well
     inside it, so the draft opens; a browser that disagrees, or an
     administrator who closed the draft, still has the button.
     §1b-i is explicit that the draft is the convenience and not a replacement:
     nothing below is conditional on this, the link stays on the screen, and an
     administrator reading it down the phone never has to open a mail client. */
  const writeInvite = (h: InviteHandout, who: string) => {
    if (!h.to) return;
    const mail = buildInviteMail({
      name: who,
      url: h.url,
      expiresAtLabel: new Date(h.expiresAt).toLocaleString(locale, {
        dateStyle: "short",
        timeStyle: "short",
      }),
      /* §1b-ii — the firm's language set, from the one place that declares it
         (`src/i18n.tsx:17`, reached through the context). Never a literal. */
      langs: supported,
      tIn,
    });
    openCompose({ to: h.to, subject: mail.subject, body: mail.body }, emailClient);
  };

  const sendInvite = async (c: CollaboratorLike, who: string) => {
    const id = c.id;
    setBusy(true);
    setErr("");
    setInvite(null);
    setInviteRefusal(null);
    try {
      const r = await sendEnrolmentInvitation(id);
      const h: InviteHandout = {
        collaboratorId: id,
        url: `${window.location.origin}/?invite=${encodeURIComponent(r.token)}`,
        expiresAt: r.expiresAt,
        hours: r.hours,
        witness: r.witness,
        to: String(c.email || "").trim(),
      };
      setInvite(h);
      writeInvite(h, who);
    } catch (e) {
      /* §3b — THE 422 HAS ITS OWN SENTENCE. `authFactors.js:654` refuses a
         suspended or archived account with `auth.account_disabled`; that is a
         real state of the record, not a fault, and it is shown as a statement
         in the muted colour rather than as a failure in red. The words are the
         SERVER's, resolved through `srvErr.<code>` in api.ts (brief 38 §3) —
         this file writes none of them, and only decides which of two ways to
         render what it is handed. */
      const f = e as ApiFailure;
      const refusal = f?.code === "auth.account_disabled";
      setInviteRefusal({
        collaboratorId: id,
        refusal,
        text:
          String(f?.message || "").trim() ||
          (refusal
            ? t("incident.invite_disabled", {
                fallback: "This account has been suspended or archived.",
              })
            : t("incident.invite_failed", {
                fallback: "The invitation could not be created.",
              })),
      });
    } finally {
      setBusy(false);
    }
  };

  /* BRIEF 98 §5 — ONE BROWSER, AND IT IS THE ONE TYPE BRANCH THIS FILE ALLOWS.
     81 §5 ruled a trusted device "INDIVIDUALLY REMOVABLE by the person,
     REMOVABLE IN BULK, removable by a firm admin". Three held; the fourth had
     the route since 83 §5 (`DELETE /trusted-devices/:id`, self OR admin,
     authFactors.js:805) and no screen. This is that screen, and no endpoint was
     added — §5b's stop never came near firing.
     THE HEADER FORBIDS ENUMERATING TYPES, and this does not break that rule: the
     ban is on deciding what to SHOW or REVOKE from a list of types this build
     happens to know, so a shape nobody here has heard of is still listed and
     still revoked — all of that is untouched above. What this asks is narrower
     and unavoidable: which rows a TYPE-SPECIFIC ROUTE accepts. The route itself
     404s anything else, so the condition is the server's, read from one named
     export rather than a literal here.
     NO CONFIRM STEP, unlike revoke-all beside it: removing one trust does not
     stamp the watermark and ends no session (authFactors.js:801) — the person
     is asked for their second step on that browser next time. It is
     housekeeping, and a confirm on housekeeping trains people through confirms
     on the act that is not. */
  const forgetDevice = async (factorId: number, subjectId: number) => {
    setBusy(true);
    setErr("");
    try {
      await removeTrustedDevice(factorId);
      const [f, l] = await Promise.all([listAuthFactorsOf(subjectId), getAuthAttemptsOf(subjectId)]);
      setFactors(f);
      setLog(l);
      toast && toast(t("incident.device_forgotten", { fallback: "That browser will ask for the second step again." }));
    } catch (e) {
      say(e, t("incident.device_forget_failed", { fallback: "That browser could not be removed." }));
    } finally {
      setBusy(false);
    }
  };

  const revokeAll = async (id: number) => {
    setBusy(true); setErr("");
    try {
      const r = await revokeAllAuthFactors(id);
      setConfirmRevoke(null);
      const [f, l] = await Promise.all([listAuthFactorsOf(id), getAuthAttemptsOf(id)]);
      setFactors(f); setLog(l);
      toast && toast(t("incident.revoked", {
        n: r.removed,
        count: r.removed, // 208 §20
        fallback: `${r.removed} credential(s) removed, and every session ended.`,
      }));
    } catch (e) {
      say(e, t("incident.revoke_failed", { fallback: "Nothing was removed." }));
    } finally {
      setBusy(false);
    }
  };

  const toggleKill = async (id: number, on: boolean) => {
    setBusy(true); setErr("");
    try {
      if (on) await killPerson(id); else await revivePerson(id);
      onKillChange && onKillChange();
      toast && toast(on
        ? t("incident.killed", { fallback: "Access cut off. They cannot sign in until it is restored." })
        : t("incident.revived", { fallback: "Access restored. Their old sessions stay ended." }));
    } catch (e) {
      // The floor's 409 arrives here as its own translated sentence via
      // `srvErr.collab.admin_floor` — this screen decides none of it.
      say(e, t("incident.kill_failed", { fallback: "That could not be done." }));
    } finally {
      setBusy(false);
    }
  };

  const people = (collaborators || []).filter((c) => c && c.id != null);
  const rows = credentials(factors?.factors);
  const attempts: AuthAttemptRow[] = (log?.attempts || []).slice(0, 12);

  return (
    <div>
      <p style={{ fontSize: FS.s11, color: "var(--tm)", margin: "0 0 6px", lineHeight: 1.5 }}>
        {t("incident.lede", {
          fallback:
            "What a colleague has set up to sign in with, where and when it has been used, and — if something has gone wrong — one action that removes every credential and ends every session they have.",
        })}
      </p>
      {/* §6, said out loud BEFORE the read rather than discovered afterwards. */}
      <p style={{ fontSize: FS.s10_5, color: "var(--tm)", margin: "0 0 10px", lineHeight: 1.5 }}>
        {t("incident.read_is_recorded", {
          fallback:
            "Opening somebody's record is itself recorded, and they are told. That is deliberate: an audit trail with a gap at its most sensitive screen has the hole exactly where it matters.",
        })}
      </p>

      {err && <div style={{ fontSize: FS.s11, color: "var(--dg, #e5484d)", margin: "0 0 8px" }}>{err}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {people.map((c) => {
          const isOpen = openId === c.id;
          const isKilled = (killed || []).includes(c.id);
          const name = [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || `#${c.id}`;
          return (
            <div key={c.id} style={{ borderBottom: "1px solid rgba(255,255,255,.04)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => open(c.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, flex: 1,
                    background: "none", border: "none", color: "inherit", cursor: "pointer",
                    textAlign: "left", fontSize: FS.s11, padding: "6px 0",
                  }}
                >
                  <span style={{ color: "var(--tm)" }}>{isOpen ? "▼" : "▶"}</span>
                  <span style={{ flex: 1 }}>{name}</span>
                  {isKilled && (
                    <span style={{ color: "var(--rd)", fontSize: FS.s10 }}>
                      {t("incident.cut_off", { fallback: "cut off" })}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className={isKilled ? "btn btn-ghost" : "btn btn-danger"}
                  disabled={busy}
                  onClick={() => toggleKill(c.id, !isKilled)}
                >
                  {isKilled
                    ? t("incident.restore_access", { fallback: "Restore access" })
                    : t("incident.cut_access", { fallback: "Cut off access" })}
                </button>
              </div>

              {isOpen && (
                <div style={{ padding: "0 0 10px 20px" }}>
                  {busy && !factors && (
                    <span style={{ fontSize: FS.s11, color: "var(--tm)" }}>
                      {t("common.loading", { fallback: "Loading…" })}
                    </span>
                  )}

                  {factors && (
                    <>
                      <div style={{ fontSize: FS.s10, color: "var(--tm)", textTransform: "uppercase", letterSpacing: ".3px", margin: "6px 0 4px" }}>
                        {t("incident.credentials", { fallback: "Credentials" })}
                      </div>
                      {rows.length === 0 ? (
                        /* BRIEF 96a §2b/§2c — THE DEAD END BECOMES THE DOOR.
                           A person with no credential at all is the one case
                           §8's third bullet ("a NEW CREDENTIAL CLASS APPEARS
                           HERE BY CONSTRUCTION") did not carry, and this is the
                           only place in the app where an administrator is
                           already looking at the fact that needs acting on. So
                           the sentence keeps its job — there is nothing to
                           revoke — and gains the one act that follows from it.
                           96 §2's separate panel is CANCELLED by 96a: mounting
                           the screen 81 §8 superseded, beside the screen that
                           superseded it, would put two surfaces on one job. */
                        <div style={{ fontSize: FS.s11, color: "var(--tm)" }}>
                          {t("incident.no_credentials", {
                            fallback: "Nothing beyond a password. There is nothing to revoke.",
                          })}
                          <div style={{ marginTop: 8 }}>
                            {invite && invite.collaboratorId === c.id ? (
                              /* §3b — SHOWN ONCE, WITH ITS EXPIRY IN THE SAME
                                 BREATH. The window is a firm setting the
                                 administrator cannot know
                                 (`authInvitationHours`, 24 by default — BRIEF
                                 103 §1, which overturned 83 §3(1)'s days), so
                                 the link is never shown without it. */
                              <div
                                style={{
                                  border: "1px solid var(--br)",
                                  borderRadius: 6,
                                  padding: 8,
                                  background: "var(--bc)",
                                }}
                              >
                                <div style={{ color: "var(--tp)", marginBottom: 6 }}>
                                  {t("incident.invite_made", {
                                    who: name,
                                    fallback: `A one-time link for ${name}. Copy it now — it is shown once and cannot be read again.`,
                                  })}
                                </div>
                                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                  <input
                                    className="field-input"
                                    readOnly
                                    value={invite.url}
                                    onFocus={(e) => e.currentTarget.select()}
                                    style={{ flex: 1, minWidth: 120, fontSize: FS.s10, padding: "2px 6px" }}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-ghost"
                                    style={{ flexShrink: 0 }}
                                    onClick={() => {
                                      /* Best-effort: a browser that refuses the
                                         clipboard leaves the field selectable,
                                         which is why the input is there and not
                                         a span. */
                                      navigator.clipboard
                                        ?.writeText(invite.url)
                                        .then(
                                          () =>
                                            toast &&
                                            toast(
                                              t("incident.invite_copied", {
                                                fallback: "Link copied.",
                                              }),
                                            ),
                                          () => {},
                                        );
                                    }}
                                  >
                                    {t("common.copy", { fallback: "Copy" })}
                                  </button>
                                  {/* §1b-i — THE DRAFT, OFFERED AGAIN.
                                      Issuing already opened one; this is the
                                      same act on a fresh click, which is the
                                      only thing a browser that refused the
                                      first `window.open` will honour. It is
                                      also what an administrator wants after
                                      closing the draft by accident, and it
                                      costs one button. */}
                                  {invite.to && (
                                    <button
                                      type="button"
                                      className="btn btn-ghost"
                                      style={{ flexShrink: 0 }}
                                      title={invite.to}
                                      onClick={() => writeInvite(invite, name)}
                                    >
                                      {t("incident.invite_draft", {
                                        fallback: "Write the email",
                                      })}
                                    </button>
                                  )}
                                </div>
                                {/* §1b-i — and when there is no address there
                                    is no draft. Said rather than shown as a
                                    button that does nothing: the link above is
                                    the whole handover in that case, which is
                                    what 96 §3b built and what still works. */}
                                {!invite.to && (
                                  <div style={{ color: "var(--tm)", marginTop: 6 }}>
                                    {t("incident.invite_no_address", {
                                      who: name,
                                      fallback: `${name} has no email address on file, so there is no draft to open — pass the link on however you choose.`,
                                    })}
                                  </div>
                                )}
                                {/* BRIEF 103 §2d — AND THE SENTENCE HAD TO
                                    CHANGE, not just its number. It read "It
                                    stops working on 25/08/2026 — 7 days, set by
                                    your firm", and under a window shorter than a
                                    day a DATE is not a useful sentence: an
                                    invitation issued at 17:00 with six hours on
                                    it stops working today, and "it stops working
                                    on 19/08/2026" reads as tomorrow's problem.
                                    So the instant is stated to the minute, and
                                    the window is stated in the unit the firm set
                                    it in. */}
                                <div style={{ color: "var(--tm)", marginTop: 6 }}>
                                  {t("incident.invite_expires_at", withCount(invite.hours, "n", { d: new Date(invite.expiresAt).toLocaleString(locale, {
                                      dateStyle: "short",
                                      timeStyle: "short",
                                    }),
                                    fallback: `It stops working on ${new Date(
                                      invite.expiresAt,
                                    ).toLocaleString(locale, {
                                      dateStyle: "short",
                                      timeStyle: "short",
                                    })} — ${invite.hours} ${invite.hours === 1 ? "hour" : "hours"}, set by your firm.`,
                                  }))}
                                </div>
                                {/* §3c — the witness is RECORDED ON the
                                    invitation and decides what acceptance
                                    needs, so it is stated rather than hidden:
                                    under `approval` the person cannot finish
                                    until an administrator approves what turned
                                    up. Issuing is never blocked by it. */}
                                {invite.witness === "approval" && (
                                  <div style={{ color: "var(--tm)", marginTop: 4 }}>
                                    {t("incident.invite_witness_approval", {
                                      fallback:
                                        "Your firm requires an administrator to approve the acceptance before this becomes an account.",
                                    })}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <>
                                {/* BRIEF 105 §1a — A BUTTON THAT LOOKS LIKE A
                                    BUTTON. `btn btn-ghost` on this screen is
                                    the same treatment as the words around it,
                                    and Laurent's reading was "now it's just
                                    regular text everywhere and one has to guess
                                    where to click". `AddButton` is the app's
                                    add affordance — 23 sites before this one,
                                    watched by
                                    `scripts/lint/add-button-owns-the-plus.js` —
                                    and creating an invitation is exactly that
                                    act. It supplies the "+" itself, so the key
                                    below must not (rule C of that lint). */}
                                <AddButton
                                  label={t("incident.invite_send", {
                                    fallback: "Create an enrolment invitation",
                                  })}
                                  disabled={busy}
                                  onClick={() => sendInvite(c, name)}
                                />
                                {inviteRefusal && inviteRefusal.collaboratorId === c.id && (
                                  <div
                                    style={{
                                      marginTop: 6,
                                      color: inviteRefusal.refusal
                                        ? "var(--tm)"
                                        : "var(--dg, #e5484d)",
                                    }}
                                  >
                                    {inviteRefusal.text}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                        rows.map((r) => (
                          <div key={r.id} style={{ fontSize: FS.s11, padding: "2px 0" }}>
                            {/* The TYPE is rendered as itself — this screen does
                                not translate a vocabulary it does not own, so a
                                shape added later shows up rather than blank. */}
                            <span>{r.label || r.type}</span>
                            <span style={{ color: "var(--tm)", marginLeft: 8 }}>{r.type}</span>
                            {r.createdAt && (
                              <span style={{ color: "var(--tm)", marginLeft: 8 }}>
                                {t("auth.card.added_on", {
                                  d: new Date(r.createdAt).toLocaleDateString(locale),
                                  fallback: `added ${new Date(r.createdAt).toLocaleDateString(locale)}`,
                                })}
                              </span>
                            )}
                            {/* §5a — only on the rows the route accepts. Every
                                other credential keeps exactly the treatment it
                                had: listed, counted, and revoked with the
                                rest. */}
                            {r.type === TRUSTED_DEVICE_TYPE && (
                              <button
                                type="button"
                                className="btn btn-ghost"
                                style={{ marginLeft: 8, padding: "1px 8px", fontSize: FS.s10 }}
                                disabled={busy}
                                onClick={() => forgetDevice(r.id, c.id)}
                              >
                                {t("auth.card.remove", { fallback: "Remove" })}
                              </button>
                            )}
                          </div>
                        ))
                      )}

                      {rows.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          {confirmRevoke === c.id ? (
                            <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                              <span style={{ fontSize: FS.s11, color: "var(--tm)" }}>
                                {t("incident.revoke_confirm", withCount(rows.length, "n", { who: name,
                                  fallback: `Remove all ${rows.length} of ${name}'s credentials and end every session they have?`,
                                }))}
                              </span>
                              <button type="button" className="btn btn-danger" disabled={busy} onClick={() => revokeAll(c.id)}>
                                {t("common.confirm", { fallback: "Confirm" })}
                              </button>
                              <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setConfirmRevoke(null)}>
                                {t("common.cancel", { fallback: "Cancel" })}
                              </button>
                            </span>
                          ) : (
                            <button type="button" className="btn btn-danger" disabled={busy} onClick={() => setConfirmRevoke(c.id)}>
                              {t("incident.revoke_all", { fallback: "Revoke everything and end their sessions" })}
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {log && (
                    <>
                      <div style={{ fontSize: FS.s10, color: "var(--tm)", textTransform: "uppercase", letterSpacing: ".3px", margin: "12px 0 4px" }}>
                        {t("incident.recent", { fallback: "Where and when it has been used" })}
                      </div>
                      {attempts.length === 0 && (
                        <div style={{ fontSize: FS.s11, color: "var(--tm)" }}>
                          {t("attempts.empty", { fallback: "Nothing recorded yet." })}
                        </div>
                      )}
                      {attempts.map((a) => (
                        <div key={a.id} style={{ display: "flex", gap: 8, fontSize: FS.s11, padding: "2px 0" }}>
                          <span style={{ color: "var(--tm)", minWidth: 128, fontVariantNumeric: "tabular-nums" }}>
                            {new Date(a.lastAt).toLocaleString(locale, {
                              day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
                            })}
                          </span>
                          <span style={{ flex: 1, color: a.outcome === "success" ? "var(--tp)" : "var(--rd)" }}>
                            {a.outcome}
                            {a.count > 1 ? ` ×${a.count}` : ""}
                          </span>
                          <span style={{ color: "var(--tm)" }}>{a.location.country || "—"}</span>
                          <span style={{ color: "var(--tm)", fontVariantNumeric: "tabular-nums" }}>{a.ip}</span>
                        </div>
                      ))}
                      <p style={{ fontSize: FS.s10, color: "var(--tm)", marginTop: 6, lineHeight: 1.5 }}>
                        {t("attempts.indication", {
                          fallback:
                            "The place is worked out from the network address using an offline database from DB-IP.com, and the device is what the browser reports: both are indications, not proof. A VPN or a mobile connection can put you in another country.",
                        })}
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
